import React, { useState, useCallback, useRef, useEffect } from 'react';

import { TextField } from '@mui/material';
import classNames from 'classnames';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import { Popover } from '../../../../../Popover';
import { Box } from '../../../../../Box';
import { Typography } from '../../../../../Typography';
import { Link } from '../../../../../Link';

import { UsersList } from './UsersList';
import { useStyles } from './MentionComponent.styles';

interface MentionComponentProps {
  updateNode: (values: {
    name: string;
    logo?: string;
    id: string;
    type: string;
  }) => void;
  initialValues?: { name: string; id: string; logo?: string; type: string };
  disableLink: boolean;
  removeHandler: () => void;
}

export const MentionComponent = ({
  updateNode,
  initialValues = { name: '', id: '', logo: '', type: '' },
  disableLink,
  removeHandler,
}: MentionComponentProps) => {
  const [editor] = useLexicalComposerContext();
  const { classes } = useStyles();

  const [inputValue, setInputValue] = useState(initialValues.name);
  const [selectedValue, setSelectedValue] = useState<{
    name: string;
    id: string;
    type: string;
    logo?: string;
  }>(initialValues);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const spanRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLElement>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (spanRef.current && inputRef.current) {
      const width = spanRef.current.offsetWidth;

      inputRef.current.style.width = `${width}px`;
    }
  }, [inputValue]);

  useEffect(() => {
    if (inputValue && updateNode) {
      editor.update(() => {
        updateNode({
          name: inputValue,
          id: '',
          logo: '',
          type: '',
        });
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    if (selectedValue.id && updateNode) {
      editor.update(() => {
        updateNode({
          name: selectedValue.name,
          id: selectedValue.id,
          logo: selectedValue.logo,
          type: selectedValue.type,
        });
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  const clearAnchorElHandler = useCallback(() => {
    setAnchorEl(() => null);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(() => e?.target?.value);

    if (anchorEl) return;
    setAnchorEl(() => e?.target);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !inputValue) {
      editor.update(() => {
        removeHandler();
      });
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setAnchorEl(() => e?.target);
  };

  const handleSelectValue = (value: {
    name: string;
    id: string;
    type: 'unit' | 'user';
    logo?: string;
  }) => {
    setSelectedValue(() => value);
  };

  if (selectedValue.id) {
    return (
      <Link
        stroke={false}
        to={`/personal/${selectedValue.id}`}
        variant="simple"
      >
        <Typography
          variant="body2"
          color="original.brand.primary"
          component="span"
        >{`@${selectedValue.name}`}</Typography>
      </Link>
    );
  }

  if (!selectedValue.id && disableLink) {
    return (
      <Typography
        variant="body2"
        color="text.light.primary"
        component="span"
      >{`@${selectedValue.name}`}</Typography>
    );
  }

  return (
    <>
      <span
        ref={spanRef}
        className={classNames([classes.span, classes.hidden])}
      >
        {inputValue}
      </span>

      <TextField
        inputRef={inputRef}
        name="mentionInput"
        variant="standard"
        type="text"
        autoComplete="off"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={clearAnchorElHandler}
        color="primary"
        slotProps={{
          input: {
            startAdornment: (
              <Typography
                variant="body2"
                color="original.brand.primary"
                component="span"
              >
                @
              </Typography>
            ),
            className: classes.input,
          },
          htmlInput: { className: classes.input },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        style={{ pointerEvents: 'none' }}
        onClose={clearAnchorElHandler}
        onMouseLeave={clearAnchorElHandler}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableAutoFocus
      >
        <Box minWidth="200" minHeight="100">
          <UsersList
            inputValue={inputValue}
            handleSelectValue={handleSelectValue}
          />
        </Box>
      </Popover>
    </>
  );
};
